import { FC, ReactElement, useState } from 'react';
import AppModal from '../app-modal/AppModal';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../util/api';

const CANCELLATION_REASON_MAX_LENGTH: number = 500;

type PatientCancelModalProps = {
  show: boolean;
  patientId: string;
  onClose: () => void;
  onSave: () => void;
}

const PatientCancelModal: FC<PatientCancelModalProps> = ({
  show,
  patientId,
  onClose,
  onSave
}: PatientCancelModalProps): ReactElement => {
  const [isCanceling, setIsCanceling] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const { authFetch } = useAuthFetchFromRestAPI();

  const handleCancel = async (): Promise<void> => {
    setErrorMessage(null);
    setIsCanceling(true);

    try {
      await authFetch(`/api/v1/patients/cancel/${patientId}`, {
        method: 'PATCH',
        body: { reason: reason.trim() || null },
      });
      onSave();
    } catch (error: unknown) {
      console.error(error);
      setErrorMessage(error instanceof Error ? error.message : i18next.t('generic.unknownError'));
    } finally {
      setIsCanceling(false);
    }
  };
    
    
  return (
    <AppModal show={show} onHide={onClose} backdrop="static" keyboard>
      <Modal.Header closeButton>
        <Modal.Title>{i18next.t('patientCancelModal.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {errorMessage && (
          <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
        )}
        
        <p>
          {i18next.t('patientCancelModal.confirmText')}
        </p>

        <Form.Group controlId="patientCancelReason">
          <Form.Label>{i18next.t('patientCancelModal.reasonLabel')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={CANCELLATION_REASON_MAX_LENGTH}
            value={reason}
            disabled={isCanceling}
            placeholder={i18next.t('patientCancelModal.reasonPlaceholder')}
            onChange={(event) => setReason(event.target.value)}
          />
        </Form.Group>

      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          type="button"
          onClick={onClose}
          disabled={isCanceling}
        >
          {i18next.t('generic.cancel')}
        </Button>
        <Button
          variant="danger"
          type="button"
          onClick={handleCancel}
          disabled={isCanceling}
        >
          {isCanceling && (
            <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
          )}
          
          {i18next.t('generic.confirm')}
        </Button>
      </Modal.Footer>
    </AppModal>
  );
};


export default PatientCancelModal;
